import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { fetchData } from "dmsbasereact";

/**
 * useReport - Hook for managing report page state and handlers
 * @param {Object} config - Configuration object
 * @param {string} config.filterOptionsUrl - URL to fetch filter options (includes status)
 * @param {string} config.exportUrl - Base URL for export
 * @param {Array} config.filterFields - Filter field definitions
 */
export const useReport = ({ filterOptionsUrl, exportUrl, filterFields: filterFieldsConfig, initialFilters = null }) => {
    const tableRef = useRef(null);
    const [filters, setFilters] = useState(initialFilters || {});
    const [clearKey, setClearKey] = useState(0);
    const [filterOptions, setFilterOptions] = useState({});
    const [columnVisibility, setColumnVisibility] = useState({});

    useEffect(() => {
        if (!filterOptionsUrl) return;

        fetchData(filterOptionsUrl, "GET", true)
            .then((response) => {
                const options = response?.data?.data || response?.data || {};
                setFilterOptions(options);
            })
            .catch(console.error);
    }, [filterOptionsUrl]);

    const filterFields = useMemo(() => {
        return filterFieldsConfig.map((field) => {
            if (field.options && field.options.length > 0) {
                return field;
            }
            if (field.optionsKey && filterOptions[field.optionsKey]) {
                const rawOptions = filterOptions[field.optionsKey];
                const options = Array.isArray(rawOptions)
                    ? rawOptions.map((v) =>
                          typeof v === "object" ? v : { value: v, label: v }
                      )
                    : [];
                return { ...field, options };
            }
            if (field.name === "status" && filterOptions.status) {
                return { ...field, options: filterOptions.status };
            }
            return field;
        });
    }, [filterFieldsConfig, filterOptions]);

    const statusMap = useMemo(() => {
        if (!filterOptions.status) return {};
        return filterOptions.status.reduce((acc, item) => {
            acc[item.value] = item.label;
            return acc;
        }, {});
    }, [filterOptions.status]);

    const handleApplyFilters = useCallback(() => {
        setClearKey((prev) => prev + 1);
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters({});
        setClearKey((prev) => prev + 1);
    }, []);

    const handleRefresh = useCallback(() => {
        setClearKey((prev) => prev + 1);
    }, []);

    const buildPreFilter = useCallback(() => {
        const preFilters = [];

        const hasValue = (value) => {
            if (value === null || value === undefined || value === "") return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
        };

        const toFilterValue = (value) =>
            Array.isArray(value) ? value.join(",") : value;

        Object.entries(filters).forEach(([name, value]) => {
            if (hasValue(value)) {
                preFilters.push({ name, value: toFilterValue(value) });
            }
        });

        return preFilters.length > 0 ? preFilters : null;
    }, [filters]);

    const buildExportUrl = useCallback(() => {
        // Return only the base export URL - ReportFilters.handleExport() handles
        // all filter/param building in the correct JSON format for the backend
        return exportUrl || null;
    }, [exportUrl]);

    return {
        state: {
            tableRef,
            filters,
            clearKey,
            filterFields,
            statusMap,
            columnVisibility,
        },
        handlers: {
            setFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefresh,
            buildPreFilter,
            buildExportUrl,
            setColumnVisibility,
        },
    };
};
